/**
 * Converts the pa_color variation <select> (single-product page,
 * woocommerce/single-product/add-to-cart/variable.php) into a row of
 * clickable color swatches — WooCommerce core only ever renders a plain
 * text dropdown for variation attributes, so this exists purely as a
 * presentation layer: the underlying <select> is kept, just visually
 * hidden, and stays the actual source of truth WooCommerce's own
 * variation.js reads from and updates. Clicking a swatch sets the
 * select's value and dispatches a native "change" event (what
 * variation.js listens for) rather than reimplementing any of the
 * price/stock/variation-matching logic itself.
 *
 * Each <option> carries a data-color-hex attribute added server-side
 * (craftis_add_color_swatch_data() in inc/woocommerce-hooks.php, reading
 * each pa_color term's craftis_color_hex term meta) — swatches with no
 * hex value (a color term added without one) are skipped, leaving that
 * option to fall back to the plain select the browser already shows.
 */

function buildSwatches( select ) {
	const wrapper = select.closest( 'td.value' ) || select.parentElement;
	if ( ! wrapper || wrapper.querySelector( '.craftis-color-swatches' ) ) {
		return;
	}

	const options = Array.from( select.options ).filter( ( option ) => option.value !== '' );
	const hasHexForAll = options.every( ( option ) => option.dataset.colorHex );

	if ( ! options.length || ! hasHexForAll ) {
		return;
	}

	const swatchList = document.createElement( 'div' );
	swatchList.className = 'craftis-color-swatches';
	swatchList.setAttribute( 'role', 'radiogroup' );

	options.forEach( ( option ) => {
		const swatch = document.createElement( 'button' );
		swatch.type = 'button';
		swatch.className = 'craftis-color-swatches__item';
		swatch.style.setProperty( '--swatch-color', option.dataset.colorHex );
		swatch.setAttribute( 'role', 'radio' );
		swatch.setAttribute( 'aria-checked', option.selected ? 'true' : 'false' );
		swatch.setAttribute( 'aria-label', option.textContent.trim() );
		swatch.title = option.textContent.trim();
		swatch.dataset.value = option.value;

		if ( option.selected ) {
			swatch.classList.add( 'is-selected' );
		}

		swatch.addEventListener( 'click', () => {
			if ( select.value === option.value ) {
				return;
			}

			select.value = option.value;
			select.dispatchEvent( new Event( 'change', { bubbles: true } ) );

			swatchList.querySelectorAll( '.craftis-color-swatches__item' ).forEach( ( item ) => {
				item.classList.remove( 'is-selected' );
				item.setAttribute( 'aria-checked', 'false' );
			} );

			swatch.classList.add( 'is-selected' );
			swatch.setAttribute( 'aria-checked', 'true' );
		} );

		swatchList.appendChild( swatch );
	} );

	select.classList.add( 'craftis-color-swatches__native-select' );
	wrapper.appendChild( swatchList );

	// The "Clear" link (a.reset_variations) is WooCommerce's own markup,
	// printed right after the <select> in the same <td> — moved here, as
	// the swatch row's last flex child, so it sits inline at the end of
	// the swatches instead of on its own line below them. Moving the
	// existing element (not cloning) keeps its bound click handler
	// (jQuery's own, wired by core's variation.js) intact.
	const resetLink = wrapper.querySelector( 'a.reset_variations' );
	if ( resetLink ) {
		swatchList.appendChild( resetLink );
	}

	// Listening for the select's own "change" event here, rather than the
	// link's click, means the swatches stay in sync however the
	// underlying value gets cleared.
	select.addEventListener( 'change', () => {
		swatchList.querySelectorAll( '.craftis-color-swatches__item' ).forEach( ( item ) => {
			const isSelected = item.dataset.value === select.value;
			item.classList.toggle( 'is-selected', isSelected );
			item.setAttribute( 'aria-checked', isSelected ? 'true' : 'false' );
		} );
	} );
}

function initColorSwatches() {
	document.querySelectorAll( 'select#pa_color, select[data-attribute_name="attribute_pa_color"]' ).forEach( buildSwatches );
}

document.addEventListener( 'DOMContentLoaded', initColorSwatches );

// WooCommerce's variation form can be re-rendered by other scripts
// (rare on a single-product page, but core does trigger this on some
// AJAX flows) — re-scanning on it is a harmless no-op via the
// already-built guard above when nothing changed.
document.body.addEventListener( 'wc_variation_form', initColorSwatches );
